<template>
  <wizard-container
    class="horizontal-search-bar"
    :class="{ 'search-bar-cal-2': calendarMonths }"
  >
    <form :action="formActionUrl" @submit="formSubmit">
      <translate
        v-if="$route.query.error === 'search-error' || showSearchError"
        tag="p"
        class="err-msg"
      >
        Unfortunately there was an error: please try again or amend your search
      </translate>
      <div class="horizontal-search-bar-content">
        <location-section
          v-if="!noLocation"
          :input-value="inputValue"
          :focused="locationInputFocused"
          :force-suggestions-open="forceSuggestionsOpen"
          :input-placeholder="inputPlaceholder"
          :default-placeholder="$t('Where are you going?')"
          :has-bounds="hasBounds"
          :search-input="suggestionsInput"
          :selected-location="selectedLocation"
          :selected-location-type="selectedLocationType"
          :suggestions="suggestions"
          tabindex="0"
          @input-changed="onLocationInputChanged"
          @location-selected="locationSelected"
          @clear-input="clearInput"
          @autosuggestion-closed="onAutoSuggestionsClosed"
          @update-suggestions="updateSuggestions($event)"
          @modify-within="modifyWithin($event)"
          @click="openLocation"
          @focus="inputIsFocused = true"
          @blur="inputIsFocused = false"
        />
        <accommodation-wizard
          :open="searchWizardStore.states.includes('categories')"
          show-summary
          :category-ids="categoryIds"
          :search-filters="searchFilters"
          :is-search-results="isSearchResults"
          :allowed-category-ids="allowedCategoryIds"
          :show-counts="
            searchWizardStore.showCount && $route.name !== 'campsite-detail'
          "
          tabindex="0"
          @open="openCategories()"
          @close="cancelEdit()"
          @cancel="cancelEdit()"
          @change-category-ids="categoryIds = $event"
        />
        <dates-wizard
          :open="searchWizardStore.states.includes('dates')"
          show-summary
          :dates="dates"
          :categories="selectedCategories"
          :availability="campsiteDatepickerAvailability"
          :opening-dates="openingDates"
          :highlight-errors="highlightErrors"
          :show-months="calendarMonths"
          tabindex="0"
          @open="openDates()"
          @cancel="cancelEdit()"
          @change-dates="dates = $event"
          @clearDates="clearDates()"
        />
        <guests-wizard
          :open="searchWizardStore.states.includes('guests')"
          :enabled="guestsEnabled"
          :clear="isClearSearch"
          show-summary
          :guests="guests"
          class="guests-wizard"
          tabindex="0"
          @open="openGuests()"
          @cancel="cancelEdit()"
          @change-guests="guests = $event"
          @click="searchWizardStore.isClearSearch = false"
        />

        <base-submit-button
          v-show="searchEnabled"
          data-test="search-wizard-submit"
          :submitting="isSearchSubmitting"
          :class="{ 'search-magnify-btn': !campsiteSlug && !isSearchResults }"
          class="search-submit-btn"
        >
          <template #default> &#8203; </template>
          <template #submittingText> &#8203; </template>
        </base-submit-button>
        <slot />
      </div>
      <div v-if="searchStepOpen" class="pagemask" @click.prevent="cancelEdit" />
    </form>
  </wizard-container>
</template>
<script lang="ts">
import { useRoute } from 'nuxt/app'
import { debounce, isEqual } from 'lodash-es'
import { CampsiteOpeningDateRange } from '~/apps/campsite-details/types'
import { getCampsiteCategoriesPresenter } from '~/apps/categories/presenter-json/CampsiteCategoriesUpdaterJson'
import { DatepickerAvailabilityBaseComponent } from '~/apps/datepicker-availability/DatepickerAvailabilityBaseComponent'
import { DatepickerAvailabilityResultsDay } from '~/apps/datepicker-availability/types'
import { resolveGeocodeFallback } from '~/apps/geo/geocodeLocation'
import { GeoCircle } from '~/utility/geo/GeoLocation'
import { Component, Prop, Watch, toNative } from '~/utility/pu-class-decorator'
import { CampsiteCategory } from '~/utility/types/CampsiteCategory'
import { getUrlPrefix } from '../../../../lang/locales'
import { logger } from '../../../../utility/logger'
import { isCampsiteDetailsRoute } from '../../../pu-links/routeHelpers'
import { OpeningDatesRange } from '../../../search-wizard/types'
import { isSearchUrlRoute } from '../../../search/isSearchUrlComponent'
import { SearchParty } from '../../../search/schemas'
import { ArriveDepartIso } from '../../../search/types'
import { SearchLinkGenerator } from '../../../url/generators/SearchLinkGenerator'
import { LocationWizardData } from '../../domain/LocationWizardData'
import DatesWizard from '../../wizards/dates/DatesWizard.vue'
import { SearchWizardFilters } from '../domain/SearchWizardFilters'
import { CampsitesSearchWizardMainProps } from './CampsitesSearchWizardMainProps'

const _isDev = process.env.NODE_ENV !== 'production'

@Component({
  components: {
    DatesWizard,
  },
  // Removed legacy Nuxt 2 fetch options and circular fetch reference
  setup() {
    const searchStore = useSearchStoreSetup(usePinia())
    const searchWizardStore = useSearchWizardStore(usePinia())

    // Consolidate categories setup here instead of using @Setup decorator
    const route = useRoute()
    const presenter = getCampsiteCategoriesPresenter(
      (route.params.lang as string) || 'en-gb',
    )

    return {
      searchStore,
      searchWizardStore,
      categories: presenter.byIdMap,
    }
  },
})
class SearchBarDesktop
  extends DatepickerAvailabilityBaseComponent
  implements CampsitesSearchWizardMainProps
{
  searchStore: ReturnType<typeof useSearchStoreSetup>
  searchWizardStore: ReturnType<typeof useSearchWizardStore>
  locationSuggestionsStore?: ReturnType<typeof useLocationSuggestionsStore>

  beforeUpdate() {
    if (this._isUnmounting) {
      return
    }
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- Vue $el type needs refinement
    if (this.$el && !this.$el.isConnected) {
      this._isUnmounting = true
      return
    }
  }

  beforeUnmount() {
    this._isUnmounting = true
    this.formSubmitting = false
  }

  async fetch() {
    const lang = this.$route?.params?.lang || 'en-gb'
    if (this.$route?.name === 'campsite-detail') {
      return await this.initAvailability(lang)
    }
  }

  @Prop({ default: false })
  showOpenCategories: boolean

  @Prop({
    default: false,
    type: Boolean,
  })
  noLocation: boolean

  @Prop({
    default: false,
    type: Boolean,
  })
  clearDatesUpdate: boolean

  @Prop()
  campsiteHierarchyPath?: string

  @Prop()
  campsiteId?: string

  @Prop()
  campsiteOpeningDateRanges?: CampsiteOpeningDateRange[]

  @Prop()
  campsitePitchCategoryIds?: number[]

  @Prop()
  campsiteCategories?: CampsiteCategory[]

  @Prop()
  currentPitchtypeId?: string

  @Prop({ required: false, type: Boolean, default: false })
  isSearchResults: boolean

  forceSuggestionsOpen = false
  showSearchError = false
  formSubmitting = false
  inputIsFocused = false
  inputValue = ''
  private _isUnmounting = false

  get context() {
    return this.searchWizardStore.context
  }

  get isClearSearch() {
    return this.searchWizardStore.isClearSearch
  }

  get suggestionsInput() {
    return this.locationSuggestionsStore?.input || ''
  }

  get suggestions() {
    return this.locationSuggestionsStore?.suggestions || {}
  }

  highlightErrors = false

  calendarMonths = 2

  async mounted() {
    this.locationSuggestionsStore = useLocationSuggestionsStore(usePinia())
    this.locationSuggestionsStore.send({
      type: 'SET_INITIAL_INPUT',
      input: this.searchInputValue,
    })
    if (this.showOpenCategories) {
      this.openCategories()
    }

    if (isCampsiteDetailsRoute(this.$route)) {
      await this.initAvailability(this.$route.params.lang || 'en-gb')
    }

    if (this.$route.query.q) {
      this.inputValue = this.$route.query.q
    }

    this.resizeListener()
    window.addEventListener('resize', this.resizeListener, { passive: true })
  }

  unmounted() {
    if (this && window && window.removeEventListener && this.resizeListener) {
      window.removeEventListener('resize', this.resizeListener)
    }
  }

  resizeListener() {
    if (document.documentElement.clientWidth < 980) {
      this.calendarMonths = 1
    } else {
      this.calendarMonths = 2
    }
  }

  updateSuggestions(input: string) {
    this.locationSuggestionsStore?.send({ type: 'SET_INPUT', input })
  }

  submit() {
    this.highlightErrors = true
  }

  clearInput() {
    logger('SearchBarDesktop.clearInput')
    this.searchWizardStore.send({ type: 'CLEAR_INPUT' })
    this.locationSuggestionsStore?.send({ type: 'SET_INPUT', input: '' })
  }

  get inputPlaceholder() {
    return this.searchWizardStore.inputPlaceholder && !this.inputIsFocused
      ? this.$t(this.searchWizardStore.inputPlaceholder).toString()
      : undefined
  }

  get searchInputValue() {
    return (
      (this.context?.searchInputValue?.type === 'CUSTOM' &&
        this.context?.searchInputValue?.text) ||
      ''
    )
  }

  set searchInputValue(value: string) {
    // this.searchWizardStore.send({ type: 'SET_INPUT_VALUE', value })
  }

  get searchFilters(): SearchWizardFilters {
    return this.searchWizardStore.wizardFilters
  }

  // Categories are now initialized in the main setup() function above
  categories: ReturnType<typeof getCampsiteCategoriesPresenter>['byIdMap']

  get selectedCategories() {
    return this.categoryIds.map((id) => this.categories[id])
  }

  get campsiteDatepickerAvailability():
    | DatepickerAvailabilityResultsDay[]
    | undefined {
    if (this.campsiteId) {
      return this.datepickerAvailability
    }
    return undefined
  }

  get openingDates(): OpeningDatesRange[] {
    if (!this.campsiteId) {
      return []
    }
    const openingDateRanges = this.campsiteOpeningDateRanges
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return -- TODO: Type openingDateRanges properly
    return openingDateRanges ? this.mapOpeningDateRanges(openingDateRanges) : []
  }

  get allowedCategoryIds(): string[] | undefined {
    if (!this.campsiteId) {
      return undefined
    }
    if (this.campsitePitchCategoryIds) {
      return this.campsitePitchCategoryIds.map((cat) => `${cat}`)
    }
    return this.campsiteCategories?.map((cat) => `${cat.id}`)
  }

  private mapOpeningDateRanges(openingDateRanges) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access -- TODO: Type openingDateRanges properly
    return openingDateRanges.map((r) => {
      return {
        from: r.openingDate, // eslint-disable-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access -- TODO: Type opening date range properly
        to: r.closingDate, // eslint-disable-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
      }
    })
  }

  onLocationInputChanged(inputValue: string) {
    this.inputValue = inputValue
  }

  async locationSelected(location: LocationWizardData, term?: string) {
    // If custom location is selected we need to retrieve point from mapbox
    if (location.dataType === 'custom') {
      try {
        const suggestionWithPoint =
          await this.locationSuggestionsStore?.loadGeoPointForLocation(location)
        location.point = suggestionWithPoint?.point
      } catch {
        this.clearInput()
        this.inputValue = ''
        this.showSearchError = true
        return
      }
    }

    this.searchWizardStore.send('SET_LOCATION', {
      data: location,
      inputValue: term,
    })

    this.locationSuggestionsStore?.send({
      type: 'SELECT_LOCATION',
      name: term || location.name,
    })
    this.cancelEdit()
    if (location.dataType === 'campsite' && !term && location.inputValue) {
      this.inputValue = location.inputValue
      return
    }
    this.inputValue = term || location.name || ''
  }

  openCategories() {
    this.searchWizardStore.send('EDIT_CATEGORIES')
  }

  get categoryIds(): string[] {
    return this.searchWizardStore.wizardFilters.categoryIds || []
  }

  set categoryIds(value: string[]) {
    this.searchWizardStore.send({
      type: 'SET_CATEGORIES',
      categoryIds: value,
    })
  }

  openLocation() {
    this.searchWizardStore.send({ type: 'FOCUS_LOCATION' })
    // Skip when q= exists: SET_INPUT already debouncing and loads recents alongside full results.
    // LOAD_RECENT_SEARCHES would interrupt the debounce, showing only recents.
    if (!this.$route?.query?.q) {
      this.locationSuggestionsStore?.send('LOAD_RECENT_SEARCHES')
    }
  }

  get locationInputFocused(): boolean {
    return this.searchWizardStore.states.includes('idle.locationFocus')
  }

  @Watch('locationInputFocused')
  onLocationInputFocusedChanged(value: boolean) {
    if (value && this.$route?.query?.q) {
      this.forceSuggestionsOpen = true
      this.updateSuggestions(this.$route.query.q)
      setTimeout(() => {
        if (!this._isUnmounting) {
          this.forceSuggestionsOpen = false
        }
      }, 1000)
    }
  }

  onAutoSuggestionsClosed() {
    this.forceSuggestionsOpen = false
    if (this.searchWizardStore.states.includes('idle.locationFocus')) {
      this.searchWizardStore.isSearchWizardOpen = false
      this.searchWizardStore.send('CANCEL_EDIT')
    }
  }

  openDates() {
    this.searchWizardStore.send('EDIT_DATES')
    this.scrollDatesWizardIntoView()
  }

  private hasScrolledInput = false

  scrollDatesWizardIntoView() {
    if (!this.hasScrolledInput) {
      const scrollTarget = document.querySelector(
        '[data-desktop-location-scroll-target]',
      ) as HTMLElement
      if (scrollTarget) {
        this.hasScrolledInput = true
        this.$puScrollTo('[data-desktop-location-scroll-target]', {
          duration: 300,
          offset: -18,
        })
      }
    }

    // setTimeout to 400ms to allow scroll above to complete before performing
    // this scroll to apply button if not visible.
    setTimeout(() => {
      // Guard: Don't execute if component is unmounting or unmounted
      if (this._isUnmounting) {
        return
      }

      const submitButton = document.querySelector(
        '[data-wizard-submit-button]',
      ) as HTMLElement

      // Guard: Element might not exist if DOM changed during timeout
      if (!submitButton) {
        return
      }

      const buttonRect = submitButton.getBoundingClientRect()
      const windowHeight =
        window.innerHeight || document.documentElement.clientHeight
      if (buttonRect.bottom >= windowHeight) {
        logger('Scroll to submitButton')
        submitButton.scrollIntoView({ block: 'end', behavior: 'smooth' })
      }
    }, 400)
  }

  get dates(): ArriveDepartIso | undefined {
    return this.searchWizardStore.wizardFilters.dates
  }

  set dates(dates: ArriveDepartIso | undefined) {
    const newDates = dates && dates.arrive ? dates : null
    this.searchWizardStore.send({
      type: 'SET_DATES',
      dateRange: newDates,
    })
  }

  get hasBounds() {
    return !!this.searchWizardStore.wizardFilters.bounds
  }

  openGuests() {
    this.searchWizardStore.send('EDIT_GUESTS')
  }

  get guests(): SearchParty {
    return (
      this.searchWizardStore.wizardFilters.party || {
        adults: 2,
        childAges: [],
        dogs: 0,
      }
    )
  }

  set guests(guests: SearchParty) {
    this.searchWizardStore.send({ type: 'SET_GUESTS', guests })
  }

  get guestsEnabled() {
    return this.$isDesktop || !!(this.dates?.arrive && this.dates?.depart)
  }

  get area(): GeoCircle | undefined {
    return this.searchWizardStore.wizardFilters.area
  }

  get searchEnabled() {
    /* eslint-disable @typescript-eslint/no-unsafe-return, @typescript-eslint/no-unsafe-call -- Route name type needs refinement */
    return (
      this.searchWizardStore.isSearchEnabled ||
      this.$route.name === 'index' ||
      this.$route.name?.includes('searchedit')
    )
    /* eslint-enable @typescript-eslint/no-unsafe-return, @typescript-eslint/no-unsafe-call */
  }

  clearDates() {
    this.searchWizardStore.send({
      type: 'CLEAR_DATES',
    })
    if (this.clearDatesUpdate) {
      this.cancelEdit()
    }
  }

  modifyWithin(within: number) {
    this.searchWizardStore.send('SET_WITHIN', { within })
  }

  cancelEdit() {
    this.searchWizardStore.send('CANCEL_EDIT')
  }

  clear() {
    this.searchWizardStore.send({ type: 'CLEAR_SEARCH_PARAMS' })
    this.locationSuggestionsStore?.send({ type: 'SET_INPUT', input: '' })
  }

  get selectedLocation() {
    return this.searchWizardStore.context.selectedLocation
  }

  get selectedLocationType() {
    return this.selectedLocation?.dataType || null
  }

  get searchStepOpen() {
    return !this.searchWizardStore.states.includes('idle')
  }

  get isEditSearch() {
    return (
      this.guests ||
      this.dates ||
      this.categoryIds.length ||
      this.searchInputValue
    )
  }

  get searchType() {
    return this.searchWizardStore?.context?.searchInputValue?.type || ''
  }

  @Watch('searchType')
  onSearchTypeChange(newVal, oldVal) {
    this.debouncedOnSearchTypeChange(this, oldVal)
  }

  debouncedOnSearchTypeChange = debounce((that, oldVal) => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- Debounced callback with dynamic `that` reference
    if (that.$route.query.q) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access -- Debounced callback with dynamic `that` reference
      that.inputValue = that.$route.query.q
      // if searchtype wasn't set before, we don't need to respond to first change
    } else if (oldVal !== '') {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- Debounced callback with dynamic `that` reference
      that.inputValue = ''
    }
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access -- Debounced callback with dynamic `that` reference
    that.updateSuggestions('')
  }, 2000)

  @Watch('searchURL', { deep: true })
  async onSearchURLChange(newVal, oldVal) {
    if (isEqual(newVal, oldVal)) {
      return
    }
    if (this.shouldDoLiveSearch()) {
      await this.formSubmit()
    }
  }

  @Watch('formActionUrl')
  async onFormActionUrlChange(newVal, oldVal) {
    if (newVal === oldVal) {
      return
    }
    logger('SearchBarDesktop.onFormActionUrlChange', newVal, oldVal)
    if (this.shouldDoLiveSearch()) {
      await this.formSubmit()
    }
  }

  shouldDoLiveSearch() {
    const currentRoute = this.$router.currentRoute.value
    if (
      !isSearchUrlRoute(currentRoute) &&
      currentRoute.name !== 'campsite-detail'
    ) {
      return false
    }
    return (
      currentRoute.name !== 'index' &&
      currentRoute.name !== 'searchedit' &&
      !this.searchWizardStore.panelOpen
    )
  }

  async formSubmit(event?) {
    try {
      this.formSubmitting = true
      if (event) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access -- Event type needs refinement
        event.preventDefault()
      }
      if (!this.searchEnabled) {
        return
      }
      if (
        !this.clearDatesUpdate &&
        this.campsiteSlug &&
        !this.searchURL.query.arrive
      ) {
        return
      }
      if (
        this.locationSuggestionsStore?.stateStrings.includes(
          'loadingCustomGeoPoint',
        )
      ) {
        await this.waitForGeoPointToLoad()
      }
      if (this.locationSuggestionsStore?.stateStrings.includes('debouncing')) {
        await this.dispatchAsyncLocationSelection()
      }
      const path = this.formActionUrl
      logger('SearchBarDesktop.formSubmit', path)

      // Critical: Clear local spinner state before navigation to prevent DOM conflicts
      // The nested layout structure (homepage-layout > default vs standalone default)
      // causes Vue's reconciliation to fail when both old and new SearchBarDesktop
      // instances try to manage LoadingSpinner components simultaneously
      this.formSubmitting = false

      // Wait for multiple ticks to ensure DOM is fully updated before navigation
      // This prevents the "Cannot read properties of null (reading 'insertBefore')" error
      await this.$nextTick()
      await new Promise((resolve) => setTimeout(resolve, 0))

      const query = { ...this.searchURL.query } as Record<
        string,
        string | string[] | undefined
      >

      // If no location was selected but user typed something,
      // pass input as q= so the search page can resolve it via geocode
      if (!this.selectedLocation && this.inputValue) {
        query.q = this.inputValue
      }
      await this.$router.push({
        path,
        query,
      })
    } catch {
      this.formSubmitting = false
    }
  }

  async dispatchAsyncLocationSelection() {
    const suggestion =
      await this.locationSuggestionsStore?.dispatchAsyncLocationSelection()
    if (suggestion) {
      await this.locationSelected(suggestion, this.inputValue)
    } else if (this.inputValue) {
      await this.geocodeFallback(this.inputValue)
    }
  }

  async geocodeFallback(inputValue: string) {
    const langCode = (this.$route.params.lang as string) || 'en-gb'
    const { $sessionId } = useNuxtApp()
    try {
      const resolved = await resolveGeocodeFallback(
        inputValue,
        langCode,
        $sessionId,
        {
          onSelecting: (name) =>
            this.locationSuggestionsStore?.send({
              type: 'SELECT_LOCATION',
              name,
            }),
          onResolved: (location) =>
            this.searchWizardStore.send('SET_LOCATION', {
              data: location,
              inputValue,
            }),
        },
      )
      if (resolved) this.inputValue = inputValue
    } catch {
      console.error(
        'SearchBarDesktop: geocodeFallback failed for input:',
        inputValue,
      )
    }
  }

  async waitForGeoPointToLoad() {
    await this.locationSuggestionsStore?.waitForMachineIdle()
    await this.$nextTick()
  }

  get formActionUrl() {
    const urlPrefix = getUrlPrefix(this.$route.params.lang)
    if (this.campsiteSlug) {
      return `${urlPrefix}/campsites/${this.campsiteHierarchyPath}${this.campsiteSlug}/`
    }
    if (this.selectedLocation?.dataType === 'campsite') {
      return `${urlPrefix}/campsites/${this.selectedLocation?.hierarchyPath}${this.selectedLocation?.slug}/`
    }
    if (this.area) {
      /* eslint-disable @typescript-eslint/no-unsafe-call -- Route name type needs refinement */
      const pathPrefix = this.$route.name?.includes('searchold')
        ? '/searchold/'
        : this.$route.name?.includes('searchnew')
          ? '/searchnew/'
          : '/search/'
      /* eslint-enable @typescript-eslint/no-unsafe-call */
      return `${urlPrefix}${pathPrefix}`
    } else {
      // TODO: check for facets url
      const path = `${urlPrefix}${this.searchURL.path}`
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call -- Route name type needs refinement
      if (this.$route.name?.includes('searchold')) {
        return path.replace('/search/', '/searchold/')
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call -- Route name type needs refinement
      } else if (this.$route.name?.includes('searchnew')) {
        return path.replace('/search/', '/searchnew/')
      }
      return path
    }
  }

  get searchURL() {
    const urlGenerator = new SearchLinkGenerator()
    const urlData = urlGenerator.getUrlData({
      category: 'search',
      excludeType: 'searchWizard',
      filters: this.searchFilters,
    })
    return urlData
  }

  get isSearchSubmitting() {
    // Don't react to store state if component is unmounting (set immediately in beforeUnmount)
    if (this._isUnmounting) {
      return false
    }

    // Also check if DOM is not connected (for cases where unmount hasn't started yet)
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- Vue $el type needs refinement
    if (this.$el && !this.$el.isConnected) {
      return false
    }

    return (
      this.formSubmitting ||
      ['searching', 'priceSearching'].some((state) =>
        this.searchStore.states.includes(state),
      )
    )
  }
}

export default toNative(SearchBarDesktop)
</script>
<style scoped>
.horizontal-search-bar {
  width: 100%;
}

.horizontal-search-bar .horizontal-search-bar-content {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  column-gap: 1em;
  flex-wrap: wrap;
  row-gap: 0.5em;
}

.horizontal-search-bar :deep(.search-form) {
  padding: 0;
  background: none;
}

.horizontal-search-bar :deep(.accommodation-wizard),
.horizontal-search-bar :deep(.dates-wizard),
.horizontal-search-bar :deep(.guests-wizard),
.horizontal-search-bar :deep(.campsite-search-wizard-main-actions),
.horizontal-search-bar :deep(.location-section .help-text) {
  display: block;
}

.horizontal-search-bar :deep(.location-section .location-options) {
  display: none;
}

.horizontal-search-bar :deep(.summary) {
  background: white;
  border-radius: 12em;
  border: 3px solid transparent;
  box-shadow: none;
  cursor: pointer;
  display: flex;
  margin: 0;
  min-height: 3em;
  padding: 0.7em 1em;
}

.horizontal-search-bar :deep(.accommodation-wizard .summary) {
  margin: 0;
  padding: 0.7em 1em;
}

.horizontal-search-bar :deep(.location-section) {
  order: 1;
  flex-shrink: 0;
}

.horizontal-search-bar :deep(.dates-wizard) {
  order: 2;
  min-width: fit-content;
}

.horizontal-search-bar :deep(.wizard-submit-button) {
  scroll-margin-bottom: 2em;
}

.search-bar-cal-2 :deep(.wizard-submit-button) {
  min-width: 18ch;
  max-width: 36ch;
  width: auto;
  display: inline-block;
}

.horizontal-search-bar.search-bar-cal-2 :deep(.date-picker-months-2 + footer) {
  margin-left: 2em;
  margin-right: 2em;
}

.horizontal-search-bar.search-bar-cal-2
  :deep(.date-picker-months-2 .date-picker__selection-key) {
  justify-content: center;
}

.horizontal-search-bar.search-bar-cal-2 :deep(.clear-dates) {
  margin-left: 1em;
}

.horizontal-search-bar :deep(.guests-wizard) {
  order: 3;
  min-width: fit-content;
}

.horizontal-search-bar :deep(.guests-wizard .guests-wizard-summary),
.horizontal-search-bar :deep(.summary .dates),
.horizontal-search-bar
  :deep(.accommodation-wizard .accommodation-wizard-summary) {
  display: flex;
  align-items: center;
}

.horizontal-search-bar :deep(.accommodation-wizard) {
  order: 4;
  min-width: fit-content;
}

.count {
  font-weight: normal;
  color: #b8ddbf;
}

.guests-wizard :deep(.guests-row) {
  gap: 1em;
  color: var(--colour-dark-blue);
}

.guests-wizard :deep(.has-children) {
  color: var(--colour-dark-blue);
}

.guests-wizard :deep(.guests-cell.children),
.guests-wizard :deep(.guests-cell.dogs) {
  display: none;
}

.guests-wizard :deep(.guests-cell.children.has-children),
.guests-wizard :deep(.has-dogs .dogs) {
  display: flex;
}

.horizontal-search-bar :deep(.accommodation-wizard-summary p) {
  display: none;
}

.horizontal-search-bar :deep(.all-categories) {
  margin: -0.5em 0;
}

.horizontal-search-bar :deep(.selected-categories) {
  margin: 0;
}

.horizontal-search-bar :deep(.all-categories) {
  margin: -0.5em 0;
}

.err-msg {
  text-align: center;
  color: var(--colour-red);
  font-size: var(--body);
  margin-bottom: 0.75em;
  font-weight: bold;
}

.search-magnify-btn {
  padding: 0;
  box-shadow: none;
  border: none;
  border-radius: 9em;
  height: 3.3em;
  width: 3.3em;
  background: var(--colour-light-green) url('~/assets/images/magnify-white.svg')
    no-repeat center center;
  background-size: 60%;
  flex-shrink: 0;
}

.search-magnify-btn {
  margin: 0 0 0 auto;
  order: 5;
}

.search-magnify-btn span {
  display: none;
}

.search-magnify-btn:hover {
  background-color: var(--colour-dark-green);
}

.search-magnify-btn.disabled {
  background-color: var(--colour-light-green);
  border: none;
  color: #fff;
  pointer-events: auto;
}

.search-magnify-btn.disabled {
  cursor: pointer;
}

.search-magnify-btn.submitting {
  box-shadow: none;
  display: flex;
  padding-left: 0;
  justify-content: center;
  align-items: center;
}

.search-magnify-btn.submitting :deep(.spinner) {
  position: static;
}

.search-magnify-btn.disabled:hover {
  background-color: var(--colour-dark-green);
}

.horizontal-search-bar :deep(.autosuggest-input) {
  background: white;
  border-radius: 9em;
  border: 3px solid #0072bc;
  font-size: 1em;
  font-weight: bold;
  padding: 0.5em 2em 0.5em 2.4em;
  margin: 0;
}

.horizontal-search-bar :deep(.icon-container.mag) {
  left: 0.25em;
  position: relative;
}

.horizontal-search-bar :deep(.icon-container.mag img) {
  filter: grayscale(1) opacity(0.5);
}

.horizontal-search-bar :deep(.pagemask:after) {
  z-index: 11;
}

.horizontal-search-bar :deep(.location-section:focus-visible),
.horizontal-search-bar :deep(.accommodation-wizard:focus-visible),
.horizontal-search-bar :deep(.dates-wizard:focus-visible),
.horizontal-search-bar :deep(.guests-wizard:focus-visible) {
  outline: none;
}

.horizontal-search-bar :deep(.accommodation-wizard:hover .summary),
.horizontal-search-bar :deep(.accommodation-wizard:focus-visible .summary),
.horizontal-search-bar :deep(.dates-wizard:hover .summary),
.horizontal-search-bar :deep(.dates-wizard:focus-visible .summary),
.horizontal-search-bar :deep(.guests-wizard:hover .summary),
.horizontal-search-bar :deep(.guests-wizard:focus-visible .summary),
.horizontal-search-bar :deep(.location-section:hover .autosuggest-input),
.horizontal-search-bar
  :deep(.location-section:focus-visible .autosuggest-input) {
  border-color: var(--colour-dark-blue);
}

.horizontal-search-bar :deep(.dates-wizard.no-dates:hover .summary),
.horizontal-search-bar :deep(.dates-wizard.no-dates:focus-visible .summary) {
  border-color: var(--colour-dark-blue);
}
</style>
